import {
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Link } from "react-router";
import type { ReplyTemplate } from "../api/useSettings.ts";
import { Kopier } from "../innstillinger/Kopier.tsx";
import { kortSubject } from "../programmer/periode.ts";
import type {
  SubmissionDetail,
  SubmissionStatus,
} from "../api/useSubmissions.ts";
import { Dialog } from "../skall/Dialog.tsx";
import { Ikon } from "../status/Ikon.tsx";
import { Pille } from "../status/Pille.tsx";
import {
  HELE_FLYTEN,
  HOVEDLINJEN,
  KATEGORI,
  KATEGORIER,
  LESER_GRUNN,
  LESER_ID,
  datoTekst,
  løfte,
  nesteSteg,
  statusEtikett,
  statusOppforing,
  stegLengerFram,
  tokenVar,
  veienVia,
} from "../status/status.ts";
import {
  INAKTIV_DAGER,
  alder,
  dagerTil,
  kortDato,
  reachTekst,
  skjermNavn,
} from "./Rad.tsx";
import { saksnr } from "./saksnummer.ts";
import { Sporingslinje, type SporLedd } from "./Sporingslinje.tsx";
import { Svar, Svarkort, type SvarTilstand } from "./Svar.tsx";

export function fristTekst(deadlineAt: string): string {
  return `Svar innen ${datoTekst(deadlineAt)}`;
}

/**
 * Leddene sporingslinjen viser (DD-56 presisert K3): Mottatt — Vurderes — «Svar innen» med
 * datoen som tall. På Mottatt er datoen den Vurderes VILLE gitt, merket «når du velger det»
 * (K4): fristen starter ikke ved innsending, og et løfte om en dato ingen har lovet ville
 * vært feil. Utenfor de to reviewing-statusene — Saker — finnes ingen frist, og siste ledd
 * er sakens nåværende status i stedet for et tomt tidsløfte.
 */
export function sporLedd(
  sak: SubmissionDetail,
  fristVedVurderes: string | null,
  nå: Date,
): SporLedd[] {
  const ny = sak.status === "ny";
  const iVurdering = ny || sak.status === "vurderes";
  const passertVurderes =
    HOVEDLINJEN.indexOf(sak.status) > HOVEDLINJEN.indexOf("vurderes");
  const ledd: SporLedd[] = [
    {
      key: "mottatt",
      navn: statusEtikett("ny", sak.type),
      under: kortDato(sak.createdAt),
      tilstand: ny ? "naa" : "ferdig",
      ikon: statusOppforing("ny").ikon,
    },
  ];
  if (iVurdering || passertVurderes)
    ledd.push({
      key: "vurderes",
      navn: statusEtikett("vurderes", sak.type),
      under: ny
        ? "når du velger det"
        : sak.status === "vurderes"
          ? `siden ${kortDato(sak.statusChangedAt)}`
          : undefined,
      tilstand: ny ? "kommende" : sak.status === "vurderes" ? "naa" : "ferdig",
      ikon: statusOppforing("vurderes").ikon,
    });
  if (iVurdering) {
    const frist = sak.deadlineAt ?? (ny ? fristVedVurderes : null);
    const over =
      sak.overdue && sak.deadlineAt
        ? Math.abs(dagerTil(sak.deadlineAt, nå))
        : 0;
    ledd.push({
      key: "frist",
      testid: "frist",
      navn: "Svar innen",
      tall: frist ? kortDato(frist) : "—",
      merknad: over ? `${over} ${over === 1 ? "dag" : "dager"} over` : undefined,
      tilstand: sak.overdue ? "brutt" : "kommende",
    });
  } else {
    ledd.push({
      key: "naa",
      navn: statusEtikett(sak.status, sak.type),
      under: kortDato(sak.statusChangedAt),
      tilstand: "naa",
      ikon: statusOppforing(sak.status).ikon,
    });
  }
  return ledd;
}

export interface DetaljHandlinger {
  /** Svaret (M2.3): aktivt valg, aldri automatisk fra notatet (DD-23). */
  svar: SvarTilstand;
  svarAapent: boolean;
  aapneSvar: () => void;
  maler: ReplyTemplate[];
  tenantName?: string;
  /** Kortet frist som ISO-dato (yyyy-mm-dd), eller tom. Bare i vurderes (DD-28). */
  frist: string;
  setFrist: (v: string) => void;
  fristGrunn: string | null;
  /** Fristen som gjelder om Vurderes velges nå — sakens egen, eller leietakerens standard fra i dag. Løftet under «Ta til vurdering» viser den (DD-56). */
  fristVedVurderes: string | null;
  valgtStatus: SubmissionStatus | null;
  setValgtStatus: (s: SubmissionStatus | null) => void;
  kandidat: string | null;
  setKandidat: (id: string | null) => void;
  notat: string;
  setNotat: (t: string) => void;
  /** Notatfeltet er utfoldet. Et notat som alt finnes vises uansett (F17). */
  notatAapent: boolean;
  aapneNotat: () => void;
  fixedIn: string;
  setFixedIn: (v: string) => void;
  notatRef: RefObject<HTMLTextAreaElement | null>;
  kanSkrive: boolean;
  opptatt: boolean;
  sperreGrunn: string | null;
  /** Hva hovedknappen gjør: «Lagre og neste» når saken får status, ellers «Send svar» eller «Lagre» — saken blir da liggende (DD-26). */
  lagreEtikett: string;
  /** Grunnen sammenslåing er låst for DEN kandidaten — null når den kan brukes. «Slå sammen» står per rad (DD-24). */
  slaaSammenGrunn: (kandidatId: string) => string | null;
  onSlaaSammen: (kandidatId: string) => void;
  onLagreOgNeste: () => void;
  /** F29: samme lagring, men saken blir stående valgt. */
  onLagre: () => void;
  /** Bare når saken får status — uten status blir den stående uansett, og én knapp holder. */
  visLagre: boolean;
  onAapneSkjermbilde: () => void;
  /** Satt når skjermbildet er hentet og skal vises i panelet. Null ellers. */
  bildeUrl: string | null;
  /** «Fjern bildet» etter bekreftelse. Kan ikke angres; saken står. */
  onFjernBilde: () => Promise<void>;
  fjernerBilde: boolean;
  onBekreftLenke: () => void;
  onFjernLenke: () => void;
  /** «Gjør til kjent feil» (M2.4): åpner registeret med sakens tekst og skjerm forhåndsutfylt. */
  onGjoerTilKjentFeil: () => void;
  /**
   * H3.1: «Grunnlag for H-142 …» under innsenderlinjen, og «Bruk som grunnlag …» ved siden av
   * «Gjør til kjent feil». Utelatt når leietakeren ikke har rommet Hypoteser eller brukeren ingen
   * rolle der — saken vises da som før. Eies av `useSakGrunnlag`.
   */
  grunnlag?: { rader: ReactNode; lenke: ReactNode };
}

/** Grunnen «Gjør til kjent feil» er låst — null når den kan brukes. Kun feil, kun fra Mottatt (domain-model.md). */
export function kjentFeilGrunn(
  sak: SubmissionDetail,
  kanSkrive: boolean,
): string | null {
  return !kanSkrive
    ? LESER_GRUNN
    : sak.knownIssue
      ? "Saken er alt en «Ja, jeg også»"
      : sak.type !== "feil"
        ? "Bare feil kan bli kjente feil"
        : sak.status !== "ny"
          ? "Kan bare gjøres fra Mottatt"
          : null;
}

/**
 * Knapp med snarveien i tittelen, som <kbd> OG som aria-keyshortcuts (DD-11). En låst knapp
 * har alltid en grunn i DOM-en: inline under knappen, eller — når grunnen er rollen — i
 * banneret øverst i detaljen, som `grunnId` peker på. Da sies grunnen én gang per skjerm,
 * ikke under hver knapp (ux-flow-architect «Forklarte låser»).
 */
export function Knapp({
  label,
  kbd,
  primaer,
  fare,
  lenke,
  status,
  grunn,
  grunnId,
  onClick,
  testid,
  fokusFørst,
}: {
  label: string;
  kbd?: string;
  primaer?: boolean;
  fare?: boolean;
  /**
   * Uten ramme, i merkefargen: en handling som ikke tar saken videre («Hele flyten», notatet).
   * Sammen med `fare` blir den rød tekst uten ramme — «Fjern» under miniatyren (DD-53).
   */
  lenke?: boolean;
  /**
   * Fylt i STATUSENS flate, ikke i action-fargen: neste steg i detaljen, og «Opprett
   * invitasjoner» på et åpent program. Foten eier den ene action-fylte knappen (DD-58,
   * presisert K2 og P4), så en handling i kroppen kan ikke være den.
   */
  status?: boolean;
  grunn?: string | null;
  grunnId?: string;
  onClick: () => void;
  testid?: string;
  /** I en Dialog: denne knappen får fokus ved åpning — den trygge i en bekreftelse. */ fokusFørst?: boolean;
}) {
  const id = `grunn-${(kbd ?? label).replace(/\W+/g, "-")}`;
  const deaktivert = Boolean(grunn);
  // Grunnen sies én gang: i banneret når den er rollen, eller — med `grunnId` — under en
  // annen knapp som deler den (F29: «Lagre» peker på grunnen under «Lagre og neste»).
  const pekerPaaAnnen =
    deaktivert && (grunn === LESER_GRUNN || grunnId !== undefined);
  return (
    <span className="panel-knapp-med-grunn">
      <button
        type="button"
        className={
          primaer
            ? "panel-knapp"
            : fare && lenke
              ? "panel-knapp-lenke panel-knapp-lenke-fare"
              : fare
                ? "panel-knapp-fare"
                : lenke
                  ? "panel-knapp-lenke"
                  : status
                    ? "panel-knapp-status"
                    : "panel-knapp-sekundaer"
        }
        title={kbd ? `${label} (${kbd})` : label}
        aria-keyshortcuts={kbd?.replace("Ctrl", "Control")}
        aria-disabled={deaktivert || undefined}
        aria-describedby={
          deaktivert ? (pekerPaaAnnen ? (grunnId ?? LESER_ID) : id) : undefined
        }
        onClick={deaktivert ? undefined : onClick}
        data-testid={testid}
        data-fokus-forst={fokusFørst || undefined}
      >
        {label}
        {kbd ? <kbd>{kbd}</kbd> : null}
      </button>
      {deaktivert && !pekerPaaAnnen ? (
        <span id={id} className="panel-grunn">
          <Ikon navn="information-circle-outline" /> {grunn}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Miniatyrens striper (R1.5, konseptets skjerm 1): de viser FORMEN på det innsenderen
 * sendte, aldri innholdet — bildet hentes først ved trykk, og det er trykket som logges.
 * Rekkefølgen er tittelfeltet, brødtekst og de maskerte partiene. Høyder og farger står i
 * `innboks.css`; her er bare mønsteret.
 */
const MASKE = [
  "topp",
  "tom",
  "full",
  "tom",
  "kort",
  "tom",
  "tom",
  "bred",
] as const;

/** Snarveien `s` + første bokstav i statusnavnet (DD-11) — bokstaven står på knappen. */
const bokstav = (s: SubmissionStatus, type: SubmissionDetail["type"]) =>
  statusEtikett(s, type)[0]?.toLowerCase() ?? "";

/**
 * Detaljen (R1.4 — innboks-konsept v0.2, DD-56, DD-57, DD-58, DD-66, DD-67):
 *
 *   spor · saksnummer · alder
 *   HELTEN: innsenderens tekst (h2) med reach som display-tallet ved siden av
 *   tillegg (DD-69) · kontekstlinjen i small
 *   [handlingsfelt: sporingslinje · Nå · Neste steg · lenkene]  [vedlegg · Ligner på · svar]
 *   festet fot: ÉN action-fylt knapp
 *
 * Sporingslinjen erstatter «Deretter: …» (K3); neste steg er fylt i STATUSENS flate, ikke
 * i action-fargen (K2); handlingsfeltet legger seg UNDER helten når det er trangt (K9).
 * Statusene som vises er de tilstandsmaskinen tillater — resten står bak «Hele flyten».
 * Svaret er åpent og notatet ligger bak en lenke (DD-66, F17); «Lagre og neste» er sperret
 * uten status (DD-26).
 */
export function Detalj({
  sak,
  h,
  nå,
}: {
  sak: SubmissionDetail;
  h: DetaljHandlinger;
  nå: Date;
}) {
  const spor =
    sak.type === "feil"
      ? { ikon: "bug-outline", navn: "Feil" }
      : { ikon: "bulb-outline", navn: "Ønske" };
  const stille = !sak.overdue && sak.inactiveDays >= INAKTIV_DAGER;
  const kandidater = sak.dedupeCandidates;
  const leser = !h.kanSkrive;
  // Fjerning bekreftes: den kan ikke angres, og den rammer det innsenderen sendte (ux-flow-architect).
  const [bekreftFjern, setBekreftFjern] = useState(false);
  const kandidatRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const steg = nesteSteg(sak.status);
  const valgt = h.valgtStatus;
  // F30 (DD-56 presisert): stegene lenger fram på hovedlinjen velges i «Hele flyten». Ett valg
  // der holder listen åpen så bekreftelsen («Saken går via …») står der valget ble gjort.
  const lengerFram = stegLengerFram(sak.status);
  const valgtLengerFram = valgt !== null && lengerFram.includes(valgt);
  const løfteFor = (s: SubmissionStatus) =>
    løfte(s, sak.type, {
      frist: s === "vurderes" ? h.fristVedVurderes : (sak.deadlineAt ?? null),
      fixedIn: h.fixedIn,
    });

  /*
    «Hele flyten» (DD-56, F30): en lenke ved siden av «Gjør til kjent feil», ikke en blokk.
    Stegene lenger fram på hovedlinjen er valgbare her — samme valg som «Neste steg», men
    uten snarvei: `s` + bokstav gjelder bare neste steg. Nåværende, forbigåtte og utgangene
    forblir tekst. Lagringen er den vanlige (Ctrl+Enter / Ctrl+S); bekreftelsen er via-linja
    under valget, ingen dialog.
  */
  const flyten = (
    <details
      className="panel-flyt"
      open={valgtLengerFram || undefined}
      data-testid="hele-flyten"
    >
      <summary>
        <Ikon navn="chevron-forward-outline" /> Hele flyten
      </summary>
      <ol className="panel-flyt-liste">
        {KATEGORIER.map((kat) => (
          <li key={kat}>
            <span className="panel-etikett">{KATEGORI[kat]}</span>
            <ul>
              {HELE_FLYTEN.filter(
                (s) => statusOppforing(s).statusType === kat,
              ).map((s) => {
                if (!lengerFram.includes(s)) {
                  return (
                    <li
                      key={s}
                      aria-current={s === sak.status ? "step" : undefined}
                    >
                      <Pille status={s} type={sak.type} liten />
                      {s === sak.status ? (
                        <span className="panel-etikett"> nå</span>
                      ) : null}
                    </li>
                  );
                }
                const på = valgt === s;
                const grunn = leser ? LESER_GRUNN : null;
                return (
                  <li key={s}>
                    <button
                      type="button"
                      aria-pressed={på}
                      aria-disabled={grunn ? true : undefined}
                      aria-describedby={grunn ? LESER_ID : undefined}
                      title={
                        grunn ??
                        `${statusEtikett(s, sak.type)} — går via ${veienVia(
                          sak.status,
                          s,
                        )
                          .map((v) => statusEtikett(v, sak.type))
                          .join(" og ")}`
                      }
                      className={`panel-neste-knapp panel-flyt-knapp${på ? " panel-neste-knapp-valgt" : ""}${grunn ? " panel-neste-knapp-laast" : ""}`}
                      onClick={
                        grunn ? undefined : () => h.setValgtStatus(s)
                      }
                      data-testid={`flyt-${s}`}
                    >
                      <Ikon navn="play-forward-outline" />
                      <Pille status={s} type={sak.type} liten />
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>
      {valgt && valgtLengerFram ? (
        <p aria-live="polite" className="panel-via" data-testid="via">
          <Ikon navn="git-commit-outline" /> Saken går via{" "}
          {veienVia(sak.status, valgt)
            .map((v) => statusEtikett(v, sak.type))
            .join(" og ")}
          . Innsenderen får ett varsel — for{" "}
          {statusEtikett(valgt, sak.type)}.
        </p>
      ) : null}
    </details>
  );

  // Piltaster i kandidatlista (radiogroup-mønsteret lover det): ned/opp flytter valget og fokus.
  const kandidatTaster = (e: KeyboardEvent<HTMLOListElement>) => {
    if (!kandidater || kandidater.length === 0) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    // Utgangspunktet er den som har fokus — før noe er valgt står fokus på den første.
    const fokusert = kandidatRefs.current.findIndex(
      (el) => el === document.activeElement,
    );
    const i =
      fokusert >= 0
        ? fokusert
        : Math.max(
            0,
            kandidater.findIndex((k) => k.id === h.kandidat),
          );
    const neste =
      e.key === "ArrowDown"
        ? Math.min(kandidater.length - 1, i + 1)
        : Math.max(0, i - 1);
    h.setKandidat(kandidater[neste]!.id);
    kandidatRefs.current[neste]?.focus();
  };

  /*
    «Ligner på» (DD-10, DD-24) som tett liste, uten boks: nummeret som snarvei, tittelen i
    body, og saksnummer, reach og status i én meta-linje. «Slå sammen» står på HVER rad, så
    valget og handlingen er samme bevegelse — `m` gjelder fortsatt den valgte. Knappen er
    tekst med <kbd>, ikke en rammet knapp (R1.5): rammen bar ingen mening listen ikke alt
    gir. Sammenslåingen sendes først etter angrevinduet (DD-10), og toasten sier det.
  */
  const lignerBlokk =
    kandidater === null ? (
      <p className="panel-tekst-liten" role="status" data-testid="ligner-paa">
        <Ikon navn="help-circle-outline" /> Fant ingen forslag til lignende saker
        akkurat nå. Søk i Saker hvis du mistenker et duplikat.
      </p>
    ) : kandidater.length === 0 ? (
      <p className="panel-tekst-liten" data-testid="ligner-paa">
        Ingen lignende saker.
      </p>
    ) : (
      <div className="panel-ligner" data-testid="ligner-paa">
        <p className="panel-etikett" id="ligner-h">
          Ligner på · {kandidater.length}
        </p>
        <ol
          className="panel-kandidater"
          role="radiogroup"
          aria-labelledby="ligner-h"
          onKeyDown={kandidatTaster}
        >
          {kandidater.map((k, i) => {
            const på = h.kandidat === k.id;
            return (
              <li key={k.id} className="panel-kandidat-rad">
                <button
                  ref={(el) => {
                    kandidatRefs.current[i] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={på}
                  tabIndex={på || (!h.kandidat && i === 0) ? 0 : -1}
                  className={`panel-kandidat${på ? " panel-kandidat-valgt" : ""}`}
                  onClick={() => h.setKandidat(k.id)}
                  data-testid={`kandidat-${i + 1}`}
                >
                  <kbd>{i + 1}</kbd>
                  <span className="panel-kandidat-tittel">{k.text}</span>
                  <span className="panel-kandidat-meta">
                    {saksnr(k.number)} · {reachTekst(k.reach)} ·{" "}
                    <Pille status={k.status} type={k.type} liten />
                  </span>
                </button>
                <Knapp
                  label="Slå sammen"
                  kbd="m"
                  lenke
                  grunn={h.slaaSammenGrunn(k.id)}
                  grunnId={
                    h.slaaSammenGrunn(k.id) === LESER_GRUNN
                      ? undefined
                      : "grunn-slaa-sammen"
                  }
                  onClick={() => h.onSlaaSammen(k.id)}
                  testid={`slaa-sammen-${i + 1}`}
                />
              </li>
            );
          })}
        </ol>
        {h.slaaSammenGrunn(kandidater[0]!.id) &&
        h.slaaSammenGrunn(kandidater[0]!.id) !== LESER_GRUNN ? (
          <span id="grunn-slaa-sammen" className="panel-grunn">
            <Ikon navn="information-circle-outline" />{" "}
            {h.slaaSammenGrunn(kandidater[0]!.id)}
          </span>
        ) : null}
      </div>
    );

  const oppsummering = [
    valgt && valgt !== sak.status
      ? `→ ${statusEtikett(valgt, sak.type)}`
      : null,
    h.svar.tekst.trim() ? "svar sendes" : null,
    h.notat.trim() ? "notat lagres" : null,
    h.frist ? "fristen kortes" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      aria-labelledby="sak-h"
      className="panel-detalj"
      data-testid="detalj"
    >
      {leser ? (
        <p
          id={LESER_ID}
          role="note"
          className="panel-melding panel-melding-info panel-leser"
          data-testid="leser-banner"
        >
          <Ikon navn="eye-outline" /> {LESER_GRUNN}.
        </p>
      ) : null}

      <header className="panel-detalj-hode">
        <p className="panel-etikett panel-detalj-meta">
          <span className="panel-detalj-spor">
            <Ikon navn={spor.ikon} /> {spor.navn}
          </span>{" "}
          {saksnr(sak.number)} · {alder(sak.createdAt, nå)}
          {sak.hasAttachment ? (
            <>
              {" · "}
              <Ikon navn="image-outline" /> skjermbilde
            </>
          ) : null}
        </p>
        {/*
          Helten (DD-67 løftet i R0.3): innsenderens egne ord i h2, og reach som det ene
          display-tallet på skjermen. Reach er et rått tall (kontrakten har `reach: int32`)
          — ingen skala, ingen fordeling per plattform. Uten identitet står «anonym», aldri
          «0»: en anonym innsending teller ikke i Reach, men saken finnes.
        */}
        <div className="panel-detalj-helt">
          <h2 id="sak-h" className="panel-detalj-tittel">
            {sak.text || <em>«Ja, jeg også» på kjent feil</em>}
          </h2>
          <p className="panel-detalj-reach" data-testid="reach">
            {sak.reach > 0 ? (
              <>
                <b className="panel-detalj-reach-tall">{sak.reach}</b>
                <span>har spurt</span>
              </>
            ) : (
              <span className="panel-detalj-reach-anonym">anonym</span>
            )}
          </p>
        </div>
      </header>

      {/*
        DD-69: innsenderens tillegg står rett under hennes egen tekst, eldste først, FØR
        kontekst og svar — det er fortsatt innsenderens ord, ikke en tråd. Nøytral kant,
        aldri brand (brand-kanten er svarets). Teksten er beslutningsgrunnlag og leses i
        body (DD-67). Kan ikke slettes enkeltvis; følger saken.
      */}
      {sak.addenda.length > 0 ? (
        <div className="panel-tillegg-liste" data-testid="tillegg">
          {sak.addenda.map((a) => (
            <div
              key={a.id}
              className="panel-tillegg"
              data-testid={`tillegg-${a.id}`}
            >
              <p className="panel-etikett panel-tillegg-hode">
                <Ikon navn="add-outline" /> Innsenderen la til ·{" "}
                {datoTekst(a.createdAt)}
              </p>
              <p className="panel-tillegg-tekst">{a.text}</p>
            </div>
          ))}
        </div>
      ) : null}

      {/*
        Kontekstlinjen: den lukkede hvitelisten (CLAUDE.md «Kontekst») på én linje som
        bryter, i small — det er meta, ikke beslutningen. Koblingsnøkkelen står som tekst;
        lenkemaler er v2.
      */}
      <p className="panel-kontekst" data-testid="kontekst">
        <span>
          <b>{sak.context.os ?? sak.context.platform ?? "—"}</b>
          {sak.context.appVersion ? ` · app ${sak.context.appVersion}` : ""}
        </span>
        {skjermNavn(sak.context.screen) ? (
          <span>
            skjerm <b>{skjermNavn(sak.context.screen)}</b>
          </span>
        ) : null}
        {sak.correlation.map((c) => (
          <span key={`${c.kind}-${c.value}`}>
            {c.kind} {c.value}
          </span>
        ))}
      </p>

      {/*
        Innsenderlinjen (M4.10, «Kundeoppslag for kundeservice»; DD-64 omgjort for panelet,
        ikke for portalen): hvem, ikke hva — rett under konteksten, i small og lesebredde.
        Pseudonymet i kortform som på programsiden; «Kopier» tar hele verdien. «Åpne kunden»
        er TJENERENS adresse (malen med pseudonymet satt inn, URL-kodet) — panelet bygger
        den aldri, og finnes den ikke, står ingenting der: det er ingen handling å låse.
        «Samme innsender» går til Saker filtrert på sakens id, aldri pseudonymet; med null
        andre saker er det tekst, ikke en lenke til en liste med én sak. Anonym sak: ingen linje.
      */}
      {sak.subject ? (
        <p className="panel-innsender" data-testid="innsender">
          <span>
            <Ikon navn="person-outline" /> Innsender{" "}
            <code title={sak.subject} data-testid="innsender-sub">
              {kortSubject(sak.subject)}
            </code>
          </span>
          <Kopier hva="pseudonymet" verdi={sak.subject} testid="innsender-kopier" />
          {sak.customerLookupUrl ? (
            <a
              href={sak.customerLookupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="panel-knapp-lenke"
              data-testid="aapne-kunden"
            >
              Åpne kunden <Ikon navn="open-outline" />
            </a>
          ) : null}
          {sak.sameSubjectCount > 0 ? (
            <Link
              to={`/saker?sameSubjectAs=${encodeURIComponent(sak.id)}`}
              className="panel-knapp-lenke"
              data-testid="samme-innsender"
            >
              Samme innsender: {sak.sameSubjectCount}{" "}
              {sak.sameSubjectCount === 1 ? "annen sak" : "andre saker"}
            </Link>
          ) : (
            <span data-testid="samme-innsender">
              Samme innsender: ingen andre saker
            </span>
          )}
        </p>
      ) : null}

      {h.grunnlag?.rader}

      <div className="panel-detalj-kropp">
        {/*
          Handlingsfeltet (DD-57 presisert K9): til høyre når det er plass, ellers UNDER
          helten og kontekstlinjen og FØR vedlegg, «Ligner på» og svaret — helten står
          alltid først. `row-reverse` + `wrap` gjør det uten en eneste mediaspørring
          (DD-51): feltet ligger først i DOM, legger seg til høyre når begge får plass, og
          bryter til egen linje over tekstkolonnen når de ikke gjør det.
        */}
        <aside
          className="panel-handlingsfelt"
          aria-label="Status og neste steg"
        >
          <Sporingslinje
            ledd={sporLedd(sak, h.fristVedVurderes, nå)}
            etikett="Hvor saken står"
          />

          <div className="panel-naa" data-testid="naa">
            <p className="panel-etikett">Nå</p>
            <Pille status={sak.status} type={sak.type} />
            {sak.overdue ? (
              <p className="panel-varsel-over">
                <Ikon navn="warning-outline" /> Avgjør nå — over frist
              </p>
            ) : null}
            {stille ? (
              <p className="panel-varsel-stille">
                <Ikon navn="pause-circle-outline" /> Ingen aktivitet på 4 uker.
              </p>
            ) : null}
            {/* Løftet ordrett (DD-08): setningen innsenderen alt har fått, ikke en omskriving. */}
            <p className="panel-tekst-liten">
              Innsenderen har fått:{" "}
              {løfte(sak.status, sak.type, {
                frist: sak.deadlineAt ?? null,
                fixedIn: sak.fixedIn ?? "",
              })}
            </p>
            {sak.knownIssue ? (
              <span className="panel-brikke">
                <Ikon navn="link-outline" /> Bekreftet på kjent feil «
                {sak.knownIssue.title}»
              </span>
            ) : null}
          </div>

          <div
            className="panel-neste"
            role="group"
            aria-label="Neste steg"
            data-testid="neste-steg"
          >
            {/* «Avgjør nå» står i «Nå» ved siden av — én gang, ikke to (F15). */}
            <p className="panel-etikett">Neste steg</p>
            {/*
              DD-58 presisert K2: neste steg er fylt i STATUSENS flate og tekst — ikke
              action-fylt; den ene action-fylte knappen står i foten. Valget vises med ring
              og `aria-pressed`, aldri ved å bytte ikon (avvik 12 i v0.2). Bare overgangene
              tilstandsmaskinen har (`KAN_GÅ_TIL`) — en vei som ikke virker, tilbys ikke.
            */}
            {steg.length === 0 ? (
              <p className="panel-tekst-liten">
                Saken er ferdig behandlet — ingen neste steg.
              </p>
            ) : (
              <div className="panel-neste-knapper">
                {steg.map((s) => {
                  const på = valgt === s;
                  const grunn = leser
                    ? LESER_GRUNN
                    : s === "avvist" && sak.reply
                      ? "Saken har allerede fått svar og kan ikke avvises"
                      : null;
                  const grunnId = `grunn-steg-${s}`;
                  const o = statusOppforing(s);
                  const etikett = statusEtikett(s, sak.type);
                  return (
                    <span key={s} className="panel-knapp-med-grunn">
                      <button
                        type="button"
                        aria-pressed={på}
                        aria-disabled={grunn ? true : undefined}
                        aria-describedby={
                          grunn
                            ? grunn === LESER_GRUNN
                              ? LESER_ID
                              : grunnId
                            : undefined
                        }
                        aria-keyshortcuts={`s ${bokstav(s, sak.type)}`}
                        title={
                          grunn ?? `${etikett} (s ${bokstav(s, sak.type)})`
                        }
                        style={
                          {
                            "--pille-fg": tokenVar(o.fg),
                            "--pille-bg": tokenVar(o.bg),
                          } as CSSProperties
                        }
                        className={`panel-neste-knapp${på ? " panel-neste-knapp-valgt" : ""}${grunn ? " panel-neste-knapp-laast" : ""}`}
                        // Avvist åpner kodene og svaret; markøren går til kodene (Svar.tsx),
                        // ikke til feltet — koden fyller malen som skal skrives om.
                        onClick={grunn ? undefined : () => h.setValgtStatus(s)}
                        data-testid={`status-${s}`}
                      >
                        <Ikon navn={o.ikon} />
                        {etikett}
                        <kbd>s {bokstav(s, sak.type)}</kbd>
                      </button>
                      {grunn && grunn !== LESER_GRUNN ? (
                        <span id={grunnId} className="panel-grunn">
                          <Ikon navn="information-circle-outline" /> {grunn}
                        </span>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            )}
            {valgt && (steg.includes(valgt) || valgtLengerFram) ? (
              <p
                aria-live="polite"
                className="panel-tekst-liten"
                data-testid="loefte"
              >
                Innsenderen får: {løfteFor(valgt)}
              </p>
            ) : null}
            {valgt === "levert" && sak.type === "feil" ? (
              <label className="panel-felt">
                <span>Rettet i versjon</span>
                <input
                  type="text"
                  value={h.fixedIn}
                  onChange={(e) => h.setFixedIn(e.target.value)}
                  placeholder="2.4.1"
                  data-testid="fixed-in"
                />
              </label>
            ) : null}
            {/*
              «Kort fristen» (K39, DD-28): funksjonen står, regelprosaen gjør det ikke (F15).
              Grensen sies som ÉN setning — datoen den nye må være før — og bare når det
              finnes en frist å korte.
            */}
            {(valgt ?? sak.status) === "vurderes" ? (
              <label className="panel-felt">
                <span>
                  <Ikon navn="hourglass-outline" /> Kort fristen
                </span>
                <input
                  type="date"
                  value={h.frist}
                  onChange={(e) => h.setFrist(e.target.value)}
                  disabled={leser}
                  aria-invalid={h.fristGrunn ? true : undefined}
                  data-testid="frist-input"
                />
                {h.fristGrunn ? (
                  <span className="panel-grunn" role="status">
                    <Ikon navn="close-circle-outline" /> {h.fristGrunn}
                  </span>
                ) : sak.deadlineAt ? (
                  <span className="panel-grunn">
                    Ny dato må være før {datoTekst(sak.deadlineAt)}.
                  </span>
                ) : null}
              </label>
            ) : null}
          </div>

          {/*
            Lenkene står UTENFOR gruppen «Neste steg»: verken «Gjør til kjent feil» eller
            «Hele flyten» er et neste steg — den første er en egen handling (DD-02), den
            andre er hoppet lenger fram (F30).
          */}
          <div className="panel-neste-lenker">
            {sak.knownIssue ? null : (
              <Knapp
                label="Gjør til kjent feil"
                kbd="f"
                lenke
                onClick={h.onGjoerTilKjentFeil}
                grunn={kjentFeilGrunn(sak, h.kanSkrive)}
                testid="gjoer-til-kjent-feil"
              />
            )}
            {h.grunnlag?.lenke}
            {flyten}
          </div>
        </aside>

        <div className="panel-detalj-tekst">
          {/*
            Beviset (R1.5, konseptets skjerm 1): den maskerte miniatyren og «Ligner på» ved
            siden av hverandre, ikke stablet. Miniatyren ER «Vis bildet» — `o` gjør det
            samme — og «Maskert · Fjern» står på én linje under (DD-53: rødt bare for det
            som fjernes på serveren). Miniatyren har flate, ikke ramme: de rammede
            elementene i detaljen er talt opp, og en ramme til hadde vært den sjuende.
          */}
          {sak.hasAttachment ? (
            <div className="panel-bevis">
              <div className="panel-bilde" data-testid="skjermbilde">
                <button
                  type="button"
                  className="panel-miniatyr"
                  aria-label={h.bildeUrl ? "Skjul bildet" : "Vis bildet"}
                  aria-keyshortcuts="o"
                  aria-pressed={Boolean(h.bildeUrl)}
                  title={`${h.bildeUrl ? "Skjul bildet" : "Vis bildet"} (o)`}
                  onClick={h.onAapneSkjermbilde}
                  data-testid="aapne-skjermbilde"
                >
                  {MASKE.map((m, i) => (
                    <span
                      key={`${m}-${i}`}
                      aria-hidden="true"
                      className={`panel-maske panel-maske-${m}`}
                    />
                  ))}
                </button>
                <p className="panel-miniatyr-linje">
                  <span>Maskert</span>
                  <Knapp
                    label="Fjern"
                    fare
                    lenke
                    onClick={() => setBekreftFjern(true)}
                    grunn={
                      leser ? LESER_GRUNN : h.fjernerBilde ? "Fjerner …" : null
                    }
                    testid="fjern-bildet"
                  />
                </p>
              </div>
              {lignerBlokk}
            </div>
          ) : (
            lignerBlokk
          )}

          {sak.hasAttachment && h.bildeUrl ? (
            <figure className="panel-vedlegg" data-testid="vedlegg">
              {/* Bøttas egen opprinnelse (K9), sluppet inn i img-src. Åpner i ny fane for full størrelse — og det er også veien til «lagre som». */}
              <a href={h.bildeUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={h.bildeUrl}
                  alt={`Skjermbilde vedlagt sak ${saksnr(sak.number)}`}
                />
              </a>
              <figcaption className="panel-etikett">
                Åpne i ny fane for full størrelse.
              </figcaption>
            </figure>
          ) : null}

          {!sak.hasAttachment && sak.attachmentRemovedAt ? (
            <p className="panel-tekst-liten" data-testid="bilde-fjernet">
              <Ikon navn="trash-outline" /> Skjermbildet er fjernet{" "}
              {new Date(sak.attachmentRemovedAt).toLocaleDateString("nb-NO", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}{" "}
              (logget).
            </p>
          ) : null}

          {bekreftFjern ? (
            <Dialog
              tittelId="fjern-bilde-h"
              onLukk={() => setBekreftFjern(false)}
              testid="fjern-bildet-dialog"
            >
              <h2 id="fjern-bilde-h" className="panel-h2">
                Fjerne bildet?
              </h2>
              <p className="panel-tekst">
                Bildet slettes for godt og kan ikke hentes tilbake. Saken står
                som den er. Innsenderen ser at bildet er fjernet — ikke hvorfor,
                og ikke av hvem. Fjerningen logges.
              </p>
              <div className="panel-handlinger">
                <Knapp
                  label="Fjern bildet"
                  fare
                  onClick={() =>
                    void h.onFjernBilde().finally(() => setBekreftFjern(false))
                  }
                  grunn={h.fjernerBilde ? "Fjerner …" : null}
                  testid="fjern-bildet-bekreft"
                />
                <Knapp
                  label="Avbryt"
                  kbd="Esc"
                  fokusFørst
                  onClick={() => setBekreftFjern(false)}
                />
              </div>
            </Dialog>
          ) : null}

          {sak.reply ? <Svarkort svar={sak.reply} /> : null}

          {sak.relatedKnownIssue ? (
            <div className="panel-boks" data-testid="related-lenke">
              <p className="panel-tekst-liten">
                <Ikon navn="link-outline" /> Innsenderen mente den ligner på «
                {sak.relatedKnownIssue.title}», men ikke helt.
              </p>
              <div className="panel-handlinger">
                <Knapp
                  label="Bekreft som samme"
                  kbd="b"
                  onClick={h.onBekreftLenke}
                  grunn={
                    leser
                      ? LESER_GRUNN
                      : sak.status !== "ny"
                        ? "Kan bare bekreftes fra Mottatt"
                        : null
                  }
                />
                <Knapp
                  label="Fjern lenke"
                  onClick={h.onFjernLenke}
                  grunn={leser ? LESER_GRUNN : null}
                />
              </div>
            </div>
          ) : null}

          <div className="panel-notat">
            {/*
              Notatene kollegene har skrevet, som tett liste uten egen ramme (R1.5, DD-66):
              de er beslutningsgrunnlag og står FØR svaret, slik saksbehandleren leser dem
              før hun skriver. Meta-linjen er datoen i small; avsenderen kan ikke vises,
              fordi notatet bærer `adminId` og ikke en adresse (DD-68).
            */}
            {sak.notes.length ? (
              <ul className="panel-notater" aria-label="Notater">
                {sak.notes.map((n) => (
                  <li key={n.id}>
                    <p className="panel-etikett">
                      <Ikon
                        navn={
                          n.origin === "sammenslaaing"
                            ? "git-merge-outline"
                            : "document-text-outline"
                        }
                      />{" "}
                      Internt notat ·{" "}
                      {n.origin === "sammenslaaing" ? (
                        <>fra sammenslått sak · </>
                      ) : null}
                      {kortDato(n.at)}
                    </p>
                    <p>{n.text}</p>
                  </li>
                ))}
              </ul>
            ) : null}

            {/*
              F17 (brukerreisene 2026-09-15): svaret til innsenderen står først og åpent.
              Det er hovedhandlingen — og av de to forvekslingene er det bare denne veien
              som oppdages. Et svar skrevet i notatfeltet når ingen, saken ser besvart ut,
              og «alle får svar» brytes i stillhet.
            */}
            {(h.svarAapent || valgt === "avvist") &&
            h.kanSkrive &&
            !sak.reply ? (
              <Svar
                s={h.svar}
                avvist={valgt === "avvist"}
                maler={h.maler}
                kanSkrive={h.kanSkrive}
              />
            ) : (
              <div className="panel-handlinger">
                <Knapp
                  label="Skriv svar til innsender"
                  kbd="r"
                  lenke
                  onClick={h.aapneSvar}
                  grunn={
                    leser
                      ? LESER_GRUNN
                      : sak.reply
                        ? "Saken har allerede fått svar"
                        : null
                  }
                  testid="skriv-svar"
                />
              </div>
            )}

            {/*
              Notatet folder seg ut på forespørsel — men aldri når det finnes noe å lese.
              Å skjule eksisterende notater ville tatt fra saksbehandleren konteksten hun
              trenger for å svare riktig (F17).
            */}
            {h.notatAapent || h.notat.trim() || sak.notes.length ? (
              <label className="panel-felt">
                <span>
                  <Ikon navn="document-text-outline" /> Internt notat{" "}
                  <span className="panel-intern-varsel">Bare for panelet</span>
                </span>
                <textarea
                  ref={h.notatRef}
                  value={h.notat}
                  onChange={(e) => h.setNotat(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Notat til deg og kollegene."
                  title="Internt notat (n)"
                  disabled={leser}
                  data-testid="notat"
                />
              </label>
            ) : (
              <div className="panel-handlinger">
                <Knapp
                  label="Skriv internt notat"
                  kbd="n"
                  lenke
                  onClick={h.aapneNotat}
                  grunn={leser ? LESER_GRUNN : null}
                  testid="skriv-notat"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="panel-detalj-fot">
        <Knapp
          label={h.lagreEtikett}
          kbd="Ctrl+Enter"
          primaer
          grunn={h.sperreGrunn}
          onClick={h.onLagreOgNeste}
          testid="lagre-og-neste"
        />
        {/* F29: «Lagre» ved siden av, for den som følger én sak. Samme sperregrunn — sagt én gang, under primærknappen. */}
        {h.visLagre ? (
          <Knapp
            label="Lagre"
            kbd="Ctrl+S"
            lenke
            grunn={h.sperreGrunn}
            grunnId={
              h.sperreGrunn === LESER_GRUNN ? undefined : "grunn-Ctrl-Enter"
            }
            onClick={h.onLagre}
            testid="lagre"
          />
        ) : null}
        {oppsummering ? (
          <span
            className="panel-fot-oppsummering"
            aria-live="polite"
            data-testid="oppsummering"
          >
            {oppsummering}
          </span>
        ) : null}
        {h.opptatt ? (
          <span role="status" className="panel-etikett">
            Lagrer …
          </span>
        ) : null}
      </footer>
    </section>
  );
}
